import type { Action, ThunkDispatch } from '@reduxjs/toolkit';
import type { IState } from '~/redux/state';
import { timelyApi } from '~redux/modules/timely/api-slice/timely.apiSlice';
import type { ProposalEntry } from '../types/types.TimesheetProposals';
import { type ClaimSpec, computeClaimEffects } from './util.entryClaim';

export type ClaimExecutionResult = {
  /** Id of the entry that ends up owning the claimed range. Undefined for
   * release claims (projectId: null) or zero-length claims (no effects). */
  targetEntryId?: string;
  /** True when the target entry was newly created rather than an existing
   * entry whose range was extended. A freshly-created entry starts with no
   * note — callers may want to regenerate one for it. */
  targetEntryCreated?: boolean;
  /** Ids of every entry mutated by this claim — useful for flash UX. */
  affectedEntryIds: string[];
};

/**
 * Executes a `ClaimSpec` against the current proposal: dispatches the
 * mutations computed by `computeClaimEffects`, batched via skipInvalidation
 * so the caller can invalidate exactly once after a sequence of claims.
 *
 * NOTE: callers running multiple ranges in sequence must refresh `entries`
 * from the cache between calls — this function executes one claim against
 * the snapshot passed in.
 */
export async function executeClaim(params: {
  claim: ClaimSpec;
  entries: ProposalEntry[];
  proposalId: string;
  dispatch: ThunkDispatch<IState, any, Action>;
}): Promise<ClaimExecutionResult> {
  const { claim, entries, proposalId, dispatch } = params;

  const { effects, targetEffectIndex } = computeClaimEffects(claim, entries);

  if (effects.length === 0) {
    return { affectedEntryIds: [] };
  }

  // Capture new entry ids from create effects so we can resolve targetEntryId.
  const createdIds: (string | undefined)[] = new Array(effects.length).fill(undefined);

  for (let i = 0; i < effects.length; i++) {
    const effect = effects[i];

    if (effect.kind === 'createEntry') {
      const result = await dispatch(
        timelyApi.endpoints.createTimesheetProposalEntry.initiate({
          proposalId,
          body: {
            date: effect.date,
            timely_project_id: effect.timely_project_id,
            timely_label_ids: effect.timely_label_ids,
            resource_id: effect.resource_id,
            note: effect.note,
            started_at: effect.started_at,
            ended_at: effect.ended_at,
          },
        })
      );
      if ('error' in result) throw result.error;
      // Rails serves the created entry id as a NUMBER; coerce to string so the
      // claim loop's subsequent patch/delete (and the string-keyed callback
      // surface) reference the right id. The cache stores the raw numeric id —
      // the transformer coerces at read — so we only normalize the loop-local id.
      createdIds[i] = String((result as any).data.entry.id);
    } else if (effect.kind === 'patchRange') {
      const result = await dispatch(
        timelyApi.endpoints.patchTimesheetProposalEntry.initiate({
          entryId: effect.entryId,
          body: {
            started_at: effect.started_at,
            ended_at: effect.ended_at,
          },
          meta: { skipInvalidation: true },
        } as any)
      );
      if ('error' in result) throw result.error;
    } else {
      // 'delete' — timely delete takes the BARE entry-id string (flattenArg). It
      // invalidates the collection tag only (never the single-proposal tag), so a
      // batch delete here can't interrupt the flash — no skipInvalidation needed.
      const result = await dispatch(
        timelyApi.endpoints.deleteTimesheetProposalEntry.initiate(effect.entryId)
      );
      if ('error' in result) throw result.error;
    }
  }

  // Determine targetEntryId.
  let targetEntryId: string | undefined;
  let targetEntryCreated = false;
  if (targetEffectIndex !== -1) {
    const targetEffect = effects[targetEffectIndex];
    if (targetEffect.kind === 'patchRange') {
      targetEntryId = targetEffect.entryId;
    } else if (targetEffect.kind === 'createEntry') {
      targetEntryId = createdIds[targetEffectIndex];
      targetEntryCreated = true;
    }
  }

  // Collect all affected entry ids (de-duplicated).
  const affectedSet = new Set<string>();
  for (let i = 0; i < effects.length; i++) {
    const effect = effects[i];
    if (effect.kind === 'createEntry') {
      const id = createdIds[i];
      if (id) affectedSet.add(id);
    } else {
      affectedSet.add(effect.entryId);
    }
  }

  return { targetEntryId, targetEntryCreated, affectedEntryIds: Array.from(affectedSet) };
}
